import { Avatar, Box, Link, Typography } from '@mui/material';
import { groupBy, sortBy, uniqBy } from 'lodash';
import moment from 'moment';
import * as React from 'react';

import { People } from '~/services/Http';

import { CalendarCell } from './components/CalendarCell';
import { useSxStyles } from './useStyles';

export interface Props {
  teamAbsences: People.AbsenceDto[];
  publicHolidays?: People.PublicHolidayDto[];
  daysInView: moment.Moment[];
  endOfRange: moment.Moment;
  minSizeRender: boolean;
  shouldRedirect: boolean;
}

export const CalendarRows: React.FC<Props> = ({
  teamAbsences,
  publicHolidays,
  daysInView,
  endOfRange,
  minSizeRender,
  shouldRedirect,
}) => {
  const sxStyles = useSxStyles();

  const sortedUniqueIds = React.useMemo(() => {
    const sortedTeamAbs = sortBy(
      teamAbsences,
      ({ employee }) => employee.firstName,
    );

    return [
      ...uniqBy(sortedTeamAbs, (abs) => abs.employee.id).map(
        (abs) => abs.employee.id,
      ),
    ];
  }, [teamAbsences]);

  const groupedAbsences = React.useMemo(() => {
    return groupBy(teamAbsences, ({ employee }) => employee.id);
  }, [teamAbsences]);

  const calendarRows = sortedUniqueIds.map((empId) => {
    const absences = groupedAbsences[empId];
    const fullName = `${absences[0].employee.firstName} ${absences[0].employee.lastName}`;
    const ref = `/page-ui2-redirect?empId=${empId}&area=people&subarea=PEOP_ABSENCES`;

    return (
      <Box key={empId} sx={sxStyles.rowWrapper}>
        <Typography
          component={'div'}
          sx={minSizeRender ? sxStyles.minNameCell : sxStyles.nameCell}
          variant="body2"
        >
          {minSizeRender &&
            (shouldRedirect ? (
              <Box component="a" sx={sxStyles.userName} href={ref}>
                <span>{`${absences[0].employee.firstName?.charAt(
                  0,
                )}. ${absences[0].employee.lastName?.charAt(0)}.`}</span>
              </Box>
            ) : (
              <span>{`${absences[0].employee.firstName?.charAt(
                0,
              )}. ${absences[0].employee.lastName?.charAt(0)}.`}</span>
            ))}

          {!minSizeRender && (
            <>
              <Avatar
                src={absences[0].employee.profilePic}
                sx={sxStyles.avatar}
              />
              {shouldRedirect ? (
                <Link sx={sxStyles.userName} href={ref}>
                  <Box component="span" sx={sxStyles.nameSpan}>
                    {fullName}
                  </Box>
                </Link>
              ) : (
                <Box component="span" sx={sxStyles.nameSpan}>
                  {fullName}
                </Box>
              )}
            </>
          )}
        </Typography>
        {daysInView.map((dayInView) => {
          return (
            <CalendarCell
              key={dayInView.format('YYYY-MM-DD')}
              day={dayInView}
              absences={absences}
              publicHolidays={publicHolidays}
              endOfRange={endOfRange}
            />
          );
        })}
      </Box>
    );
  });

  return <>{calendarRows}</>;
};
